import { useCallback, useEffect, useRef, useState } from "react";
import { FEEDBACK_TIMINGS } from "../../../constants/feedback-timings";
import { handleAsyncAction } from "../../../utils/async-error-handler";
import { getLangfuseClient } from "@/services/langfuse-client";

type UseUserFeedbackProps = {
  readonly traceId?: string;
  readonly observationId?: string;
};

export const FEEDBACK_POSITIVE_VALUE = 1;
// `0` is intentional; avoid truthy checks and compare against constants explicitly.
export const FEEDBACK_NEGATIVE_VALUE = 0;

export function useUserFeedback({ traceId, observationId }: UseUserFeedbackProps) {
  const [feedback, setFeedback] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [selectedComment, setSelectedComment] = useState<string | null>(null);
  const [showTextArea, setShowTextArea] = useState(false);
  const [freeTextComment, setFreeTextComment] = useState("");
  const commentHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(false);
  const requestIdRef = useRef(0);

  const clearCommentHideTimeout = useCallback(() => {
    if (commentHideTimeoutRef.current !== null) {
      clearTimeout(commentHideTimeoutRef.current);
      commentHideTimeoutRef.current = null;
    }
  }, []);

  const invalidatePendingRequest = useCallback(() => {
    requestIdRef.current += 1;
    clearCommentHideTimeout();
  }, [clearCommentHideTimeout]);

  const isActiveRequest = useCallback((requestId: number) => (
    isMountedRef.current && requestIdRef.current === requestId
  ), []);

  // Keep trace identifiers captured here so other handlers can depend on this callback.
  const submitToLangfuse = useCallback(async (value: number, comment: string) => {
    const langfuseWeb = getLangfuseClient();
    if (!langfuseWeb) {
      throw new Error("Langfuse client not available");
    }

    await langfuseWeb.score({
      traceId,
      observationId,
      name: "user-feedback",
      dataType: "NUMERIC",
      value,
      comment,
    });
  }, [observationId, traceId]);

  const handleUserFeedback = useCallback((value: number) => {
    if (isSubmitting)
      return;

    invalidatePendingRequest();
    setFeedback(value);
    setSelectedComment(null);
    setFreeTextComment("");
    setShowComments(value === FEEDBACK_POSITIVE_VALUE);
    setShowTextArea(value === FEEDBACK_NEGATIVE_VALUE);

    // Initial feedback is best effort; reason submissions remain independent.
    void submitToLangfuse(value, "").catch(() => undefined);
  }, [invalidatePendingRequest, isSubmitting, submitToLangfuse]);

  const handleCommentSelection = useCallback(async (comment: string) => {
    if (isSubmitting || feedback === null)
      return;

    invalidatePendingRequest();
    const requestId = requestIdRef.current;

    // If "Something else" is selected, show text area instead
    if (comment === "Something else") {
      setSelectedComment(comment);
      setShowTextArea(true);
      return;
    }

    await handleAsyncAction(
      async () => {
        setSelectedComment(comment);
        await submitToLangfuse(feedback, comment);

        if (!isActiveRequest(requestId))
          return;

        // Hide comments section after brief active state display
        commentHideTimeoutRef.current = setTimeout(() => {
          commentHideTimeoutRef.current = null;
          if (!isActiveRequest(requestId))
            return;
          setShowComments(false);
        }, FEEDBACK_TIMINGS.ACTIVE_STATE_DISPLAY_MS);
      },
      {
        setIsSubmitting: (submitting) => {
          if (isActiveRequest(requestId))
            setIsSubmitting(submitting);
        },
        errorContext: "submit comment feedback",
        onError: () => {
          if (isActiveRequest(requestId))
            setSelectedComment(null);
        },
      },
    );
  }, [feedback, invalidatePendingRequest, isActiveRequest, isSubmitting, submitToLangfuse]);

  const handleFreeTextSubmit = useCallback(async (text: string) => {
    if (isSubmitting || feedback === null)
      return;

    invalidatePendingRequest();
    const requestId = requestIdRef.current;

    await handleAsyncAction(
      async () => {
        await submitToLangfuse(feedback, text);

        // Hide section immediately after successful submission (no timeout for user input)
        if (isActiveRequest(requestId)) {
          setFreeTextComment("");
          setShowComments(false);
          setShowTextArea(false);
        }
      },
      {
        setIsSubmitting: (submitting) => {
          if (isActiveRequest(requestId))
            setIsSubmitting(submitting);
        },
        errorContext: "submit free text feedback",
      },
    );
  }, [feedback, invalidatePendingRequest, isActiveRequest, isSubmitting, submitToLangfuse]);

  const handleBackToComments = useCallback(() => {
    if (isSubmitting)
      return;

    invalidatePendingRequest();
    setShowTextArea(false);
    setSelectedComment(null);
    setFreeTextComment("");
    setShowComments(feedback === FEEDBACK_POSITIVE_VALUE);
  }, [feedback, invalidatePendingRequest, isSubmitting]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      requestIdRef.current += 1;
      clearCommentHideTimeout();
    };
  }, [clearCommentHideTimeout]);

  // Computed values
  const shouldShowComments = showComments && !showTextArea;
  // Kept as hook surface: CommentOptions chooses positive vs negative presets from this.
  const isPositive = feedback === FEEDBACK_POSITIVE_VALUE;
  const langfuseWeb = getLangfuseClient();
  // Hide component if environment variables are missing or other required data is missing
  const isVisible = !!traceId && !!observationId && !!langfuseWeb;

  return {
    // State
    feedback,
    isSubmitting,
    selectedComment,
    freeTextComment,

    // Computed values
    shouldShowComments,
    shouldShowTextArea: showTextArea,
    isPositive,
    isVisible,

    // Handlers
    handleUserFeedback,
    handleCommentSelection,
    handleFreeTextSubmit,
    setFreeTextComment,
    handleBackToComments,
  };
}
